/**
 * POST the page context to the Control Plane (plan 1968 F3 endpoint).
 *
 * Same pattern as fetchAiSso.ts: raw fetch, NO Authorization header, the CP JWT
 * in the body as `access_token`, `credentials: "omit"`, after ensureFreshToken().
 *
 * NEVER use cpGuestApiCall here: its second 401 clears the CP token = logs the
 * user out. This is a background beacon — every failure is swallowed silently
 * (debug log in dev only): 401 bad JWT, 403 tenant mismatch, 417 oversize, 404
 * not deployed, network errors.
 */

import { CONTROL_PLANE_URL, getCPToken } from "@/lib/api/controlPlaneClient";
import { ensureFreshToken } from "@/lib/store/authStore";
import type { PageContext } from "./types";

export const SET_PAGE_CONTEXT_METHOD = "control_plane.api.ai.set_page_context";

function debug(...args: unknown[]): void {
  if (import.meta.env.DEV) console.debug("[page-context]", ...args);
}

export async function postPageContext(context: PageContext): Promise<boolean> {
  try {
    await ensureFreshToken();
    const token = getCPToken();
    if (!token) return false;
    const response = await fetch(`${CONTROL_PLANE_URL}/api/method/${SET_PAGE_CONTEXT_METHOD}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      keepalive: true,
      body: JSON.stringify({ access_token: token, context }),
    });
    if (!response.ok) {
      debug("set_page_context", response.status);
      return false;
    }
    return true;
  } catch (error) {
    debug("set_page_context failed", error);
    return false;
  }
}
